import { withTranslation } from "react-i18next";
import WithRouter from "../../../../components/Common/WithRouter";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { Button } from "primereact/button";
import { Col, Row } from "react-bootstrap";
import { get, isEmpty } from "lodash";
import StateLessGrid from "../../../../components/Shared/StateLessGrid";
import QuickCopyDropdown from "../../../../components/Shared/QuickCopyDropdown";
import Tooltip from "../../../../components/Common/Display/Tooltip";
import VinWithType from "../../../../components/Common/Display/VinWithType";
import OrderStatus from "../../../../components/Common/Display/OrderStatus";
import Location from "../../../../components/Common/Display/Location";
import { utcDateToLocalDate } from "../../../../utils/Helper";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";

const ACTIVE_LOAD_STATUSES = [
    "added-to-load",
    "load-accepted",
    "load-rejected",
    "in-transit",
    "unload-request",
];

const VinSearchList = (props: any) => {
    const { t, moduleState, setQuery } = props;
    const tableRef: any = useRef();
    const [selectedRows, setSelectedRows] = useState<any[]>([]);
    const [vinInput, setVinInput] = useState<string>("");

    const vinString = get(moduleState, ["filters", "vin_numbers"]) ?? "";
    const parseVinText = useCallback((text: any) => {
        if ( !text || isEmpty(text) ) {
            return [];
        }

        return String(text)
            .split(/[\s,]+/)
            .map((vin) => String(vin).trim())
            .filter((vin) => !!vin)
            .map((vin) => vin.toUpperCase());
    }, []);

    const vinList = useMemo(() => {
        const parsed = parseVinText(vinString);
        const unique: string[] = [];
        const seen = new Set<string>();

        parsed.forEach((vin) => {
            const key = vin.toUpperCase();
            if ( !seen.has(key) ) {
                seen.add(key);
                unique.push(vin);
            }
        });

        return unique;
    }, [vinString, parseVinText]);

    const updateVinNumbers = useCallback((nextVins: string[]) => {
        const nextString = Array.isArray(nextVins) ? nextVins.filter((vin) => !!vin).join(" ") : "";
        setQuery({
            ...moduleState,
            page: 1,
            filters: {
                ...(moduleState.filters || {}),
                vin_numbers: nextString,
            },
        });
    }, [moduleState, setQuery]);

    const onAddVins = useCallback(() => {
        const toAdd = parseVinText(vinInput);
        if ( toAdd.length === 0 ) {
            return;
        }

        const merged: string[] = [];
        const seen = new Set<string>();
        [...vinList, ...toAdd].forEach((vin) => {
            const key = vin.toUpperCase();
            if ( !seen.has(key) ) {
                seen.add(key);
                merged.push(vin);
            }
        });

        updateVinNumbers(merged);
        setVinInput("");
    }, [parseVinText, updateVinNumbers, vinInput, vinList]);

    const onRemoveVin = useCallback((vinToRemove: string) => {
        const needle = String(vinToRemove || "").toUpperCase();
        updateVinNumbers(vinList.filter((vin) => String(vin).toUpperCase() !== needle));
    }, [updateVinNumbers, vinList]);

    const openDetail = (row: any) => {
        const id = get(row, ["id"]);
        if ( !id ) {
            return;
        }

        const status = get(row, ["status"]);
        const loadDetailId = get(row, ["load_detail_id"]);
        const loadDetailType = get(row, ["load_detail_type"]);
        const loadDetailNumber = get(row, ["load_detail_number"]) || "";

        if ( ACTIVE_LOAD_STATUSES.includes(status) && loadDetailId && loadDetailType ) {
            const loadUrl = `/loads?operation=detail&operationId=${ encodeURIComponent(loadDetailId) }&detailType=${ encodeURIComponent(loadDetailType) }&detailLoadNumber=${ encodeURIComponent(loadDetailNumber) }`;
            window.open(loadUrl, "_blank", "noopener,noreferrer");
            return;
        }

        window.open(`/orders?operation=detail&operationId=${ encodeURIComponent(id) }`, "_blank", "noopener,noreferrer");
    };

    const columns = [
        {
            field: "vin_number",
            title: t("vin number"),
            sorting: true,
            sortField: "orders.vin_number",
            render: (_row: any) => (
                <a className={ "cursor-pointer" } onClick={ () => openDetail(_row) }>
                    <VinWithType
                        vinNumber={ _row.vin_number }
                        type={ _row.source_type }
                        priorisation={ _row.priorisation }
                        builtToOrderCd={ _row.built_to_order_cd }
                    />
                </a>
            ),
        },
        {
            field: "order_cd",
            title: t("order"),
            sorting: true,
            sortField: "orders.order_cd",
        },
        {
            field: "brand",
            title: t("brand") + "/" + t("model"),
            sorting: true,
            sortField: "orders.brand_id",
            render: ({ brand, brand_model }: any) => (
                <div className={ "text-capitalize" }>
                    { brand?.title }
                    { brand?.alternate_title && <div>{ brand.alternate_title }</div> }
                    <div>
                        { brand_model?.title }
                        { brand_model?.version_code && <div>{ brand_model.version_code }</div> }
                    </div>
                </div>
            ),
        },
        {
            field: "client",
            title: t("client"),
            sorting: true,
            sortField: "orders.client_id",
            render: ({ client }: any) => <div className={ "text-capitalize" }>{ client?.name }</div>,
        },
        {
            field: "provider",
            title: t("provider"),
            sorting: true,
            sortField: "orders.provider_id",
            render: ({ provider }: any) => <div className={ "text-capitalize" }>{ provider?.title || "-" }</div>,
        },
        {
            field: "status",
            title: t("status"),
            sorting: true,
            sortField: "orders.status",
            render: (_row: any) => <OrderStatus status={ _row.status } />,
        },
        {
            field: "from_location",
            title: t("current location"),
            sorting: false,
            render: (_row: any) => {
                const currentJourney = get(_row, ["current_journey"]);
                const location = _row.status === "delivered"
                    ? get(currentJourney, ["to_location"])
                    : get(currentJourney, ["from_location"]);

                return location ? <Location item={ location } titled={ 1 } /> : null;
            },
        },
        {
            field: "updated_at",
            title: t("modified at"),
            sorting: true,
            sortField: "orders.updated_at",
            render: ({ updated_at }: any) => updated_at && (
                <Tooltip tooltip={ utcDateToLocalDate(updated_at) }>
                    { utcDateToLocalDate(updated_at) }
                </Tooltip>
            ),
        },
        {
            field: "action",
            title: t("action"),
            sorting: false,
            align: "right",
            render: (_row: any) => (
                <Tooltip tooltip={ t("detail of item") }>
                    <a onClick={ () => openDetail(_row) } className={ "ms-2 cursor-pointer" }>
                        <i className="fas fa-eye" />
                    </a>
                </Tooltip>
            ),
        },
    ];

    return (
        <div className="block-viewer">
            <div className="block-section">
                <div className="block-header">
                    <span className="block-title">
                        <h5 className={ "mb-0" }>{ t("vin numbers") }</h5>
                    </span>
                </div>
                <div className="block-content">
                    <Row className={ "p-2" }>
                        <Col sm={ 12 } className={ "mb-2" }>
                            <div className={ "d-flex flex-wrap gap-2 align-items-center" }>
                                { vinList.map((vin: string) => (
                                    <Tag
                                        key={ vin }
                                        rounded
                                        value={
                                            <span className={ "d-flex align-items-center gap-2" }>
                                                <span>{ vin }</span>
                                                <i
                                                    className={ "pi pi-times cursor-pointer" }
                                                    onClick={ () => onRemoveVin(vin) }
                                                />
                                            </span>
                                        }
                                    />
                                )) }
                            </div>

                            <div className={ "d-flex flex-wrap gap-2 align-items-center mt-2" }>
                                <InputText
                                    value={ vinInput }
                                    onChange={ (e) => setVinInput(e.target.value) }
                                    placeholder={ t("a series of vin number values, each separated by a space or comma") }
                                    className={ "w-100 md:w-30rem" }
                                    onKeyDown={ (e) => {
                                        if ( e.key === "Enter" ) {
                                            e.preventDefault();
                                            onAddVins();
                                        }
                                    } }
                                />
                                <Button
                                    type={ "button" }
                                    size={ "small" }
                                    icon={ "pi pi-plus" }
                                    label={ t("add") }
                                    onClick={ onAddVins }
                                    disabled={ isEmpty(vinInput) }
                                />
                            </div>
                        </Col>

                        <Col sm={ 12 } className={ "text-right mb-2" }>
                            <div className="d-inline-block me-2 align-middle text-left">
                                <QuickCopyDropdown
                                    columns={ columns }
                                    tableName={ "supplierVinSearchList" }
                                    selectedRows={ selectedRows }
                                    trackBy={ "id" }
                                    entities={ tableRef.current?.getEntities?.() || [] }
                                />
                            </div>
                        </Col>

                        <Col sm={ 12 }>
                            <StateLessGrid
                                size={ "normal" }
                                url={ "/suppliers/search/vins" }
                                method={ "POST" }
                                name={ "supplierVinSearchList" }
                                columns={ columns }
                                moduleState={ moduleState }
                                setQuery={ setQuery }
                                ref={ tableRef }
                                itemSize={ 100 }
                                state={ true }
                                stickyHeader={ true }
                                onRowSelection={ setSelectedRows }
                                selectedRows={ selectedRows }
                                showQuickCopy={ false }
                            />
                        </Col>
                    </Row>
                </div>
            </div>
        </div>
    );
};

export default withTranslation()(WithRouter(VinSearchList));
